import { HTTPClient, propsToDatePaged } from "@abaco/web-common/src/HTTP";
import { PagedResults, PagingParams } from "@abaco/web-common/src/Paging";
import { ProtocolBbch, ProtocolVariety, Variety, VarietyFilter, VarietyId, Vegcycle } from "../models/VarietyAndPhase";

export default class ProtocolApi {
    private http: HTTPClient;
    private rootPath!: string;

    constructor(http: HTTPClient) {
        this.http = http;
        this.rootPath = "/sitiagri-rest-api/api_basic/v1"
    }

    public async getProtocolVarieties(pk_cuaa: number, protocol_id: number, paging: PagingParams): Promise<PagedResults<ProtocolVariety>> {
        const res = await this.http.get(`${this.rootPath}/protocols/subjects/${pk_cuaa}/dictionary_protocols/${protocol_id}/varieties`, {
            params: {
                count: paging.count,
                offset: paging.offset
            }
        })

        return res.data;
    }

    public async getVariety(pk_cuaa: number, seed_variety_id: number): Promise<Variety | null> {
        const res = await this.http.get(`${this.rootPath}/warehouse/subjects/${pk_cuaa}/seeds`, {
            params: {
                count: 1,
                offset: 0,
                filter: `seed_variety_id eq ${seed_variety_id}`
            }
        })

        return res.data.count > 0 ? res.data.records[0] : null;
    }

    public async getDictionaryVariety(pk_cuaa: number, paging: PagingParams, filter: VarietyFilter, alreadyPresent: number[]): Promise<PagedResults<Variety>> {
        const filterValues: string[] = [];

        filter.item_name ? filterValues.push(`item_name like ${filter.item_name.toUpperCase()}`) : '';
        filter.item_code ? filterValues.push(`item_code like ${filter.item_code.toUpperCase()}`) : '';
        filter.ds_precocity ? filterValues.push(`ds_precocity like ${filter.ds_precocity.toUpperCase()}`) : '';
        filter.ds_agm_group ? filterValues.push(`ds_agm_group like ${filter.ds_agm_group.toUpperCase()}`) : '';

        if (alreadyPresent.length) {
            alreadyPresent.forEach(f => {
                filterValues.push(`seed_variety_id ne ${f}`)
            })
        }
        const res = await this.http.get(`${this.rootPath}/warehouse/subjects/${pk_cuaa}/seeds`, {
            params: {
                count: paging.count,
                offset: paging.offset,
                filter: filterValues
            }
        })

        return res.data;
    }

    public async getPhases(protocol_id: number): Promise<ProtocolBbch[]> {
        const res = await this.http.get(`${this.rootPath}/protocols/dictionary/${protocol_id}/bbch`, {
            params: {
                offset: 0,
                count: 300
            }
        })

        return res.data.records;
    }

    public async getPhase(protocol_id: number, bbch_code: string): Promise<ProtocolBbch> {
        const res = await this.http.get(`${this.rootPath}/protocols/dictionary/${protocol_id}/bbch`, {
            params: {
                offset: 0,
                count: 1,
                filter: `bbch_code eq ${bbch_code}`
            }
        })

        return res.data.records[0];
    }

    public async getVegetativeCycles(plot_id: number): Promise<Vegcycle[]> {
        const res = await this.http.get(`${this.rootPath}/plots/plots/${plot_id}/vegetative_cycles`, {
            params: {
                count: 300,
                offset: 0
            }
        })

        propsToDatePaged(res.data, ["end_date", "start_date"])

        return res.data.records;
    }

    public async removeVariety(pk_cuaa: number, protocol_id: number, variety_id: number): Promise<ProtocolVariety> {
        const res = await this.http.delete(`${this.rootPath}/protocols/subjects/${pk_cuaa}/dictionary_protocols/${protocol_id}/varieties/${variety_id}`);

        return res.data;
    }

    public async addNewVariety(pkCuaa: number, protocol_id: number, newVarieties: VarietyId[]): Promise<ProtocolVariety[]> {
        const res = await this.http.post(`${this.rootPath}/protocols/subjects/${pkCuaa}/dictionary_protocols/${protocol_id}/varieties`, { varieties: newVarieties });

        return res.data;
    }
}
